import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTenantVerified } from '../lib/onboarding';
import { GatewayRole, GatewayRoleLabels, QboConnectionStatus } from '../lib/enums';
import { qbo } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { initials } from '../lib/format';
import { Chip, ConnectionStatusChip } from './Chips';

interface NavItem {
  to: string;
  label: string;
  minRole: GatewayRole;
  group: 'Work' | 'Partners' | 'QuickBooks' | 'Configuration';
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', minRole: GatewayRole.Auditor, group: 'Work' },
  // Setup help sits at the top of Work because it is the first thing a new company needs, and it
  // stays reachable before the licence is verified (see UNVERIFIED_ROUTES).
  { to: '/assistant', label: 'Assistant', minRole: GatewayRole.Auditor, group: 'Work' },
  { to: '/documents', label: 'Documents', minRole: GatewayRole.Auditor, group: 'Work' },
  // Identifying and teaching a supplier is the step BEFORE their invoices are processed, so it sits
  // above Vendor Invoice Intake. A vendor can also be taught from its own card; this is the entry
  // for a supplier that has no card yet.
  { to: '/vendor-intake', label: 'Vendor Intake', minRole: GatewayRole.Operator, group: 'Work' },
  { to: '/vendor-invoice-intake', label: 'Vendor Invoice Intake', minRole: GatewayRole.Auditor, group: 'Work' },
  { to: '/purchase-invoices', label: 'Purchase Invoices', minRole: GatewayRole.Auditor, group: 'Work' },
  { to: '/approvals', label: 'Approvals', minRole: GatewayRole.Auditor, group: 'Work' },
  // Their own group: a trading partner is neither one of this company's work queues nor a
  // QuickBooks record. Placed after Work and before QuickBooks because a connection has to exist
  // before anything it sends can appear in a queue.
  { to: '/partners/directory', label: 'Company Directory', minRole: GatewayRole.Operator, group: 'Partners' },
  { to: '/partners/connections', label: 'Trading Partners', minRole: GatewayRole.Operator, group: 'Partners' },
  { to: '/partners/deliveries', label: 'Partner Deliveries', minRole: GatewayRole.Operator, group: 'Partners' },
  { to: '/partners/outbound', label: 'Send to Partners', minRole: GatewayRole.Operator, group: 'Partners' },
  { to: '/vendors', label: 'Vendors', minRole: GatewayRole.Auditor, group: 'QuickBooks' },
  { to: '/accounts', label: 'Accounts', minRole: GatewayRole.Auditor, group: 'QuickBooks' },
  { to: '/purchase-orders', label: 'Purchase Orders', minRole: GatewayRole.Auditor, group: 'QuickBooks' },
  { to: '/expenses', label: 'Expenses & Payments', minRole: GatewayRole.Auditor, group: 'QuickBooks' },
  { to: '/history', label: 'Sync History', minRole: GatewayRole.Auditor, group: 'QuickBooks' },
  { to: '/sync-rules', label: 'Sync Rules', minRole: GatewayRole.Admin, group: 'Configuration' },
  { to: '/email-intake', label: 'Email Intake', minRole: GatewayRole.Admin, group: 'Configuration' },
  { to: '/payables-agent', label: 'Payables Agent', minRole: GatewayRole.Admin, group: 'Configuration' },
  { to: '/ai-settings', label: 'AI Settings', minRole: GatewayRole.Admin, group: 'Configuration' },
  { to: '/ai-learning', label: 'AI Learning', minRole: GatewayRole.Admin, group: 'Configuration' },
  { to: '/platform', label: 'Platform', minRole: GatewayRole.Operator, group: 'Configuration' },
  { to: '/settings', label: 'Settings', minRole: GatewayRole.Auditor, group: 'Configuration' },
];

const GROUP_ORDER: NavItem['group'][] = ['Work', 'Partners', 'QuickBooks', 'Configuration'];

/** The only destinations an unverified tenant has: their status, their own account settings, and setup help. */
const UNVERIFIED_ROUTES = new Set(['/', '/settings', '/assistant']);

export function AppLayout(): JSX.Element {
  const { user, signOut } = useAuth();
  const verified = useTenantVerified();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Not persisted: this app keeps no browser storage (see lib/api.ts), so every group opens
  // expanded on a fresh load — the state only tracks what the person has collapsed this session.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<NavItem['group']>>(() => new Set());

  // A single lightweight probe so the top bar can always show connection health.
  const connection = useAsync((signal) => qbo.connection(signal), []);

  const role = user?.role ?? GatewayRole.Auditor;
  const visibleItems = NAV_ITEMS.filter(
    (item) => role >= item.minRole && (verified || UNVERIFIED_ROUTES.has(item.to)),
  );
  const currentTitle =
    NAV_ITEMS.find((item) => item.to === location.pathname)?.label ??
    (location.pathname.startsWith('/purchase-invoices') ? 'Purchase Invoices' : 'Gateway');

  // Navigating into a collapsed group's own page (e.g. a bookmark or a back-button return) should
  // reveal it rather than leave the active link hidden behind a closed header.
  useEffect(() => {
    const activeGroup = NAV_ITEMS.find((item) => item.to === location.pathname)?.group;
    if (activeGroup) {
      setCollapsedGroups((current) => {
        if (!current.has(activeGroup)) return current;
        const next = new Set(current);
        next.delete(activeGroup);
        return next;
      });
    }
  }, [location.pathname]);

  const toggleGroup = (group: NavItem['group']) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  return (
    <div className={`app-shell ${navOpen ? 'nav-open' : ''}`.trim()}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <aside className="sidebar" aria-label="Primary">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            iE
          </span>
          <span className="brand-text">
            <span className="brand-name">iERP Gateway</span>
            <span className="brand-sub">QuickBooks Online</span>
          </span>
        </div>

        <nav className="nav">
          {GROUP_ORDER.map((group) => {
            const items = visibleItems.filter((item) => item.group === group);
            if (items.length === 0) return null;
            const collapsed = collapsedGroups.has(group);
            return (
              <div className={`nav-group ${collapsed ? 'nav-group-collapsed' : ''}`.trim()} key={group}>
                <button
                  type="button"
                  className="nav-group-label"
                  aria-expanded={!collapsed}
                  onClick={() => toggleGroup(group)}
                >
                  {group}
                  <svg className="nav-group-chevron" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M3 4.5 6 8l3-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <ul className="nav-list">
                  {items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === '/'}
                        className={({ isActive }) => `nav-link ${isActive ? 'nav-link-active' : ''}`}
                        onClick={() => setNavOpen(false)}
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          {connection.data ? (
            <div className="sidebar-connection">
              <ConnectionStatusChip status={connection.data.status} />
              {connection.data.isSandbox ? <Chip tone="warn">Sandbox</Chip> : null}
              <p className="sidebar-company" title={connection.data.companyName ?? undefined}>
                {connection.data.companyName ?? 'No company linked'}
              </p>
            </div>
          ) : connection.error ? (
            <div className="sidebar-connection">
              <Chip tone="bad">Connection unknown</Chip>
            </div>
          ) : null}
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <button
            type="button"
            className="btn btn-ghost btn-icon nav-toggle"
            aria-label={navOpen ? 'Hide navigation' : 'Show navigation'}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((open) => !open)}
          >
            ☰
          </button>

          <div className="topbar-context">
            <span className="topbar-title">{currentTitle}</span>
            {user ? <span className="topbar-tenant">{user.tenantName}</span> : null}
          </div>

          <div className="topbar-right">
            {connection.data && connection.data.status === QboConnectionStatus.ExpiringSoon ? (
              <Chip tone="warn">
                Re-consent in {connection.data.daysUntilReconsentRequired ?? 0} days
              </Chip>
            ) : null}
            {user ? (
              <div className="user-chip">
                <span className="avatar" aria-hidden="true">
                  {initials(user.displayName || user.email)}
                </span>
                <span className="user-meta">
                  <span className="user-name">{user.displayName || user.email}</span>
                  <span className="user-role">{GatewayRoleLabels[user.role]}</span>
                </span>
              </div>
            ) : null}
            <button type="button" className="btn btn-secondary btn-sm" onClick={signOut}>
              Sign out
            </button>
          </div>
        </header>

        <main className="content" id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      {navOpen ? (
        <button
          type="button"
          className="nav-scrim"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
        />
      ) : null}
    </div>
  );
}
